import React from 'react';
import type { Currency } from '@hero/schema';
import { formatNumberWithCommas, formatCompactNumber } from '@hero/core';
import { cn } from '@hero/web-lib';

const SAR_GLYPH = '\u00EA';

const SIZE_CLASSES: Record<string, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

/**
 * `<CurrencySymbol>` — renders the branded currency glyph.
 *
 * For SAR we render the official Saudi Riyal glyph from the
 * `SaudiRiyalSymbol` font. The glyph is intentionally scaled to
 * `0.9em` so its optical weight matches the numeric font at any
 * parent size (the raw font metrics render slightly heavier than
 * proportional sans-serif digits at large sizes). Non-SAR currencies
 * fall through to the plain ISO code text and inherit their
 * surrounding typography untouched.
 *
 * The scale is *proportional* (em-based), not absolute, so this
 * looks correct at `text-sm` on a form field and at `text-3xl`
 * on a marketing hero — no per-caller tweaking needed.
 */
export const CurrencySymbol: React.FC<{ currency: Currency }> = ({ currency }) => {
  if (currency === 'SAR') {
    return (
      <span className="font-saudi-riyal text-[0.9em] leading-none">{SAR_GLYPH}</span>
    );
  }
  return <>{currency}</>;
};

export interface PriceProps {
  amount: number | undefined;
  currency?: Currency;
  className?: string;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | 'inherit';
  /** "full" = 1,500,000 (default), "compact" = 1.5M / 200K / 1K / 500 */
  format?: 'full' | 'compact';
}

/**
 * `<Price>` — canonical currency+amount renderer for the web
 * dashboards + landing pages.
 *
 * The gap between glyph and number is rendered as a right margin on
 * the glyph (`me-[0.2em]`) rather than a bare space character so it
 * stays proportional at every parent font-size — a bare space
 * inherits the parent font's space-glyph width, which balloons to
 * ~10-15px at display sizes and looks visually broken. Using an em
 * unit keeps the gap tight and consistent across `text-sm` forms and
 * `text-display-md` marketing headers.
 */
export const Price: React.FC<PriceProps> = ({
  amount = 0,
  currency = 'SAR',
  className,
  size = 'inherit',
  format = 'full',
}) => {
  const sizeClass = size === 'inherit' ? undefined : SIZE_CLASSES[size];
  const formatted =
    format === 'compact' ? formatCompactNumber(Math.abs(amount)) : formatNumberWithCommas(amount);

  return (
    <span className={cn('inline-flex items-baseline', sizeClass, className)}>
      <span className="me-[0.2em]">
        <CurrencySymbol currency={currency} />
      </span>
      <span>{formatted}</span>
    </span>
  );
};
